/**
 * KommunikationOverlayContent Component
 * Displays detailed Communication data in the overlay
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, FileText, TrendingUp, Grid3X3, Radar, DollarSign } from 'lucide-react';
import { AnalysisChart } from '@/components/charts/AnalysisChart/AnalysisChart';
import { getEffectivenessColor } from '@/types/kommunikation';
import type { KommunikationFullData } from '@/types/kommunikation';
import type { ChartConfig } from '@/types/api';
import { SectionDescriptionText } from '@/components/results/SectionDescriptionText';
import type { GetDescriptionFn } from '@/types/sectionDescription';

export interface KommunikationOverlayContentProps {
  data: KommunikationFullData | string;
  getDescription?: GetDescriptionFn;
  featureName?: string;
}

// Brand colors for multiple series
const CHANNEL_COLORS = ['#FC03B0', '#FF6803', '#E8148A', '#22c55e', '#3b82f6'];

/**
 * Overlay content for Kommunikation with full details
 */
export function KommunikationOverlayContent({
  data,
  getDescription,
  featureName,
}: KommunikationOverlayContentProps) {
  const { t } = useTranslation();
  const [selectedRadarChannel, setSelectedRadarChannel] = useState<number | null>(null);

  // Handle legacy string data
  if (typeof data === 'string') {
    return (
      <div className="space-y-6">
        <div className="hidden md:flex items-center gap-2 md:gap-3 pb-1 md:pb-2">
          <div className="p-2 md:p-2.5 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
            <MessageSquare className="h-5 w-5 md:h-6 md:w-6 text-[var(--color-primary-start)]" />
          </div>
          <h3 className="text-base md:text-lg font-semibold leading-none tracking-tight text-[var(--color-charcoal)]">
            {t('results.cards.kommunikation.title')}
          </h3>
        </div>
        <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
          <p className="text-sm text-[var(--color-charcoal)]/80 leading-relaxed whitespace-pre-wrap">
            {data}
          </p>
        </div>
      </div>
    );
  }

  const { main } = data;

  // Get top 5 channels for the overlay
  const topChannels = main.Kommunikation_Top_Kanäle?.slice(0, 5) || [];

  // Create scatter chart config for Kanal-Produkt-Fit
  const scatterChartConfig = useMemo((): ChartConfig | null => {
    const matrix = main.Kommunikation_Produkt_Fit_Matrix;
    if (!matrix || !matrix.data || matrix.data.length === 0) {
      return null;
    }

    const seriesData = matrix.data.map(point => ({
      x: point.x_value,
      y: point.y_value,
    }));

    return {
      type: 'scatter',
      series: [{
        name: 'Kanäle',
        data: seriesData,
      }] as unknown as ChartConfig['series'],
      options: {
        chart: {
          type: 'scatter',
          zoom: { enabled: false },
          toolbar: { show: false },
        },
        grid: {
          padding: { top: 10, right: 20, bottom: 10, left: 10 },
        },
        xaxis: {
          min: matrix.encoding.x.start,
          max: matrix.encoding.x.end,
          tickAmount: 9,
          title: {
            text: t('results.cards.kommunikation.overlay.productFit'),
            style: { fontSize: '11px', color: '#32373c' },
          },
          labels: { style: { fontSize: '10px', colors: '#32373c' } },
        },
        yaxis: {
          min: matrix.encoding.y.start,
          max: matrix.encoding.y.end,
          tickAmount: 9,
          title: {
            text: t('results.cards.kommunikation.overlay.effectiveness'),
            style: { fontSize: '11px', color: '#32373c' },
          },
          labels: { style: { fontSize: '10px', colors: '#32373c' } },
        },
        colors: ['#FC03B0'],
        markers: {
          size: 10,
          colors: ['#FC03B0'],
          strokeColors: '#fff',
          strokeWidth: 2,
          hover: { size: 12 },
        },
        annotations: {
          position: 'back',
          yaxis: [{
            y: 5,
            borderColor: '#e0e0e0',
            strokeDashArray: 4,
          }],
          xaxis: [{
            x: 5,
            borderColor: '#e0e0e0',
            strokeDashArray: 4,
          }],
        },
        tooltip: {
          custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
            const point = matrix.data[dataPointIndex];
            return `<div class="p-2 bg-white shadow-lg rounded text-xs border">
              <strong class="text-sm">${point.name}</strong><br/>
              <span class="text-[var(--color-charcoal)]/70">Produkt-Fit:</span> <strong>${point.x_value}</strong><br/>
              <span class="text-[var(--color-charcoal)]/70">Effektivität:</span> <strong>${point.y_value}</strong>
            </div>`;
          },
        },
        legend: { show: false },
      } as ChartConfig['options'],
    };
  }, [main.Kommunikation_Produkt_Fit_Matrix, t]);

  // Create heatmap chart config
  const heatmapChartConfig = useMemo((): ChartConfig | null => {
    const heatmapData = main['Kommunikation_Kanal-Milieu-Zuordnung'];
    if (!heatmapData || !heatmapData.data || heatmapData.data.length === 0) {
      return null;
    }

    const xFeatures = heatmapData.encoding.x.features;
    const yFeatures = heatmapData.encoding.y.features;

    // Build series for ApexCharts heatmap (rows are series, columns are x-axis)
    const series = yFeatures.map(yFeature => {
      const rowData = xFeatures.map(xFeature => {
        const dataPoint = heatmapData.data.find(
          d => d.x_value === xFeature.index && d.y_value === yFeature.index
        );
        return dataPoint?.value || 0;
      });
      return {
        name: yFeature.name,
        data: rowData,
      };
    });

    return {
      type: 'heatmap',
      series: series as unknown as ChartConfig['series'],
      options: {
        chart: {
          type: 'heatmap',
          toolbar: { show: false },
        },
        dataLabels: {
          enabled: true,
          style: { fontSize: '11px', fontWeight: 'bold', colors: ['#fff'] },
        },
        xaxis: {
          categories: xFeatures.map(f => f.name),
          labels: { style: { fontSize: '10px', colors: '#32373c' } },
        },
        yaxis: {
          labels: { style: { fontSize: '10px', colors: '#32373c' } },
        },
        colors: ['#FC03B0'],
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.5,
            radius: 4,
            colorScale: {
              ranges: [
                { from: 0, to: 3, color: '#fecdd3', name: 'Niedrig' },
                { from: 4, to: 6, color: '#f472b6', name: 'Mittel' },
                { from: 7, to: 10, color: '#be185d', name: 'Hoch' },
              ],
            },
          },
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${val}/10`,
          },
        },
        legend: { show: false },
      } as ChartConfig['options'],
    };
  }, [main['Kommunikation_Kanal-Milieu-Zuordnung']]);

  // Create bubble scatter chart config for Kosten-Nutzen
  const bubbleChartConfig = useMemo((): ChartConfig | null => {
    const bubbleData = main['Kommunikation_Kosten-Nutzen-Vergleich'];
    if (!bubbleData || !bubbleData.data || bubbleData.data.length === 0) {
      return null;
    }

    // Use scatter with different sized markers to simulate bubbles
    const seriesData = bubbleData.data.map(point => ({
      x: point.x_value,
      y: point.y_value,
    }));

    return {
      type: 'scatter',
      series: [{
        name: 'Kanäle',
        data: seriesData,
      }] as unknown as ChartConfig['series'],
      options: {
        chart: {
          type: 'scatter',
          zoom: { enabled: false },
          toolbar: { show: false },
        },
        grid: {
          padding: { top: 10, right: 20, bottom: 10, left: 10 },
        },
        xaxis: {
          min: bubbleData.encoding.x.start,
          max: bubbleData.encoding.x.end,
          tickAmount: 9,
          title: {
            text: t('results.cards.kommunikation.overlay.costPerLead'),
            style: { fontSize: '11px', color: '#32373c' },
          },
          labels: { style: { fontSize: '10px', colors: '#32373c' } },
        },
        yaxis: {
          min: bubbleData.encoding.y.start,
          max: bubbleData.encoding.y.end,
          tickAmount: 9,
          title: {
            text: t('results.cards.kommunikation.overlay.conversionRate'),
            style: { fontSize: '11px', color: '#32373c' },
          },
          labels: { style: { fontSize: '10px', colors: '#32373c' } },
        },
        colors: ['#FF6803'],
        markers: {
          size: 12,
          colors: ['#FF6803'],
          strokeColors: '#fff',
          strokeWidth: 2,
          hover: { size: 14 },
        },
        annotations: {
          position: 'back',
          yaxis: [{ y: 5, borderColor: '#e0e0e0', strokeDashArray: 4 }],
          xaxis: [{ x: 5, borderColor: '#e0e0e0', strokeDashArray: 4 }],
        },
        tooltip: {
          custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
            const point = bubbleData.data[dataPointIndex];
            return `<div class="p-2 bg-white shadow-lg rounded text-xs border">
              <strong class="text-sm">${point.name}</strong><br/>
              <span class="text-[var(--color-charcoal)]/70">Kosten:</span> <strong>${point.x_value}</strong><br/>
              <span class="text-[var(--color-charcoal)]/70">Abschlussquote:</span> <strong>${point.y_value}</strong>
            </div>`;
          },
        },
        legend: { show: false },
      } as ChartConfig['options'],
    };
  }, [main['Kommunikation_Kosten-Nutzen-Vergleich'], t]);

  // Create radar chart config
  const radarChartConfig = useMemo((): ChartConfig | null => {
    const radarData = main['Kommunikation_Kanal-Reifegrad-Radar'];
    if (!radarData || !radarData.data || radarData.data.length === 0) {
      return null;
    }

    const categories = radarData.encoding.features.map(f => f.name);

    // If a channel is selected, only show that one; otherwise show all
    const dataToShow = selectedRadarChannel !== null
      ? [radarData.data[selectedRadarChannel]]
      : radarData.data;

    const series = dataToShow.map((channel) => {
      const values = radarData.encoding.features.map(feature => {
        const dim = channel.dimensions.find(d => d.feature_index === feature.feature_index);
        return dim?.value || 0;
      });
      return {
        name: channel.kanal,
        data: values,
      };
    });

    const colorsToUse = selectedRadarChannel !== null
      ? [CHANNEL_COLORS[selectedRadarChannel % CHANNEL_COLORS.length]]
      : CHANNEL_COLORS.slice(0, radarData.data.length);

    return {
      type: 'radar',
      series: series as unknown as ChartConfig['series'],
      options: {
        chart: {
          type: 'radar',
          toolbar: { show: false },
        },
        grid: {
          padding: { top: -10, right: 0, bottom: -10, left: 0 },
        },
        xaxis: {
          categories: categories,
          labels: {
            show: true,
            style: { fontSize: '10px', colors: Array(categories.length).fill('#32373c') },
          },
        },
        yaxis: {
          show: false,
          min: radarData.notes.value_range[0],
          max: radarData.notes.value_range[1],
          tickAmount: 5,
        },
        colors: colorsToUse,
        fill: { opacity: 0.2 },
        stroke: { width: 2 },
        markers: {
          size: 4,
          strokeColors: '#fff',
          strokeWidth: 1,
        },
        plotOptions: {
          radar: {
            size: 110,
            polygons: {
              strokeColors: '#e0e0e0',
              strokeWidth: 1,
              connectorColors: '#e0e0e0',
              fill: { colors: ['#fafafa', '#fff'] },
            },
          },
        },
        tooltip: {
          y: { formatter: (val: number) => `${val}/5` },
        },
        legend: {
          show: true,
          position: 'bottom',
          fontSize: '11px',
          height: 40,
          offsetY: 0,
        },
      } as ChartConfig['options'],
    };
  }, [main['Kommunikation_Kanal-Reifegrad-Radar'], selectedRadarChannel]);

  const radarData = main['Kommunikation_Kanal-Reifegrad-Radar'];

  return (
    <div className="space-y-6">
      {/* Header with icon and title */}
      <div className="hidden md:flex items-center gap-2 md:gap-3 pb-1 md:pb-2">
        <div className="p-2 md:p-2.5 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
          <MessageSquare className="h-5 w-5 md:h-6 md:w-6 text-[var(--color-primary-start)]" />
        </div>
        <h3 className="text-base md:text-lg font-semibold leading-none tracking-tight text-[var(--color-charcoal)]">
          {t('results.cards.kommunikation.title')}
        </h3>
      </div>

      {/* Top 5 Channels */}
      <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-5 w-5 text-[var(--color-primary-start)]" />
          <h4 className="text-sm font-semibold text-[var(--color-charcoal)]">
            {t('results.cards.kommunikation.overlay.topChannels')}
          </h4>
          <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="topChannels" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {topChannels.map((channel) => (
            <div
              key={channel.Position}
              className="p-3 bg-white rounded-[var(--radius-sm)] border border-[var(--glass-border)] relative overflow-hidden"
            >
              {/* Position badge */}
              <div
                className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                style={{
                  background: channel.Position === 1
                    ? 'linear-gradient(135deg, #FC03B0, #FF6803)'
                    : channel.Position === 2
                      ? 'linear-gradient(135deg, #E8148A, #FC03B0)'
                      : channel.Position === 3
                        ? '#f97316'
                        : '#9ca3af',
                }}
              >
                {channel.Position}
              </div>
              <p className="font-semibold text-[var(--color-charcoal)] text-sm mb-1 pr-8">
                {channel.Name}
              </p>
              <p className="text-xs text-[var(--color-charcoal)]/60 mb-2">
                {channel.Angesprochenes_Milieu}
              </p>
              <div className="flex items-center gap-1">
                <div
                  className="h-1.5 flex-1 bg-gray-200 rounded-full overflow-hidden"
                >
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${(channel.Wirkungsgrad / 10) * 100}%`,
                      backgroundColor: getEffectivenessColor(channel.Wirkungsgrad),
                    }}
                  />
                </div>
                <span
                  className="text-xs font-bold min-w-[32px] text-right"
                  style={{ color: getEffectivenessColor(channel.Wirkungsgrad) }}
                >
                  {channel.Wirkungsgrad}/10
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Grid - 2x2 layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Kanal-Produkt-Fit Matrix (Scatter) */}
        <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-5 w-5 text-[var(--color-primary-start)]" />
            <h4 className="text-sm font-semibold text-[var(--color-charcoal)]">
              {t('results.cards.kommunikation.overlay.channelProductFitMatrix')}
            </h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="channelProductFit" />
          </div>
          <p className="text-xs text-[var(--color-charcoal)]/60 mb-2">
            {main.Kommunikation_Produkt_Fit_Matrix?.notes?.interpretation_hint || ''}
          </p>
          {scatterChartConfig ? (
            <AnalysisChart config={scatterChartConfig} height={260} />
          ) : (
            <div className="h-[260px] flex items-center justify-center">
              <span className="text-sm text-[var(--color-charcoal)]/50">
                {t('results.cards.kommunikation.overlay.noDataAvailable')}
              </span>
            </div>
          )}
        </div>

        {/* Kanal-Milieu-Zuordnung (Heatmap) */}
        <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-3">
            <Grid3X3 className="h-5 w-5 text-[var(--color-primary-start)]" />
            <h4 className="text-sm font-semibold text-[var(--color-charcoal)]">
              {t('results.cards.kommunikation.overlay.channelMilieuMapping')}
            </h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="channelMilieuMapping" />
          </div>
          <p className="text-xs text-[var(--color-charcoal)]/60 mb-2">
            {main['Kommunikation_Kanal-Milieu-Zuordnung']?.notes?.interpretation_hint || ''}
          </p>
          {heatmapChartConfig ? (
            <AnalysisChart config={heatmapChartConfig} height={260} />
          ) : (
            <div className="h-[260px] flex items-center justify-center">
              <span className="text-sm text-[var(--color-charcoal)]/50">
                {t('results.cards.kommunikation.overlay.noDataAvailable')}
              </span>
            </div>
          )}
        </div>

        {/* Kosten-Nutzen-Vergleich (Bubble Scatter) */}
        <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="h-5 w-5 text-[var(--color-primary-end)]" />
            <h4 className="text-sm font-semibold text-[var(--color-charcoal)]">
              {t('results.cards.kommunikation.overlay.costBenefitComparison')}
            </h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="costBenefit" />
          </div>
          <p className="text-xs text-[var(--color-charcoal)]/60 mb-2">
            {main['Kommunikation_Kosten-Nutzen-Vergleich']?.notes?.interpretation_hint || ''}
          </p>
          {bubbleChartConfig ? (
            <AnalysisChart config={bubbleChartConfig} height={260} />
          ) : (
            <div className="h-[260px] flex items-center justify-center">
              <span className="text-sm text-[var(--color-charcoal)]/50">
                {t('results.cards.kommunikation.overlay.noDataAvailable')}
              </span>
            </div>
          )}
        </div>

        {/* Kanal-Reifegrad-Radar */}
        <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Radar className="h-5 w-5 text-[var(--color-primary-start)]" />
              <h4 className="text-sm font-semibold text-[var(--color-charcoal)]">
                {t('results.cards.kommunikation.overlay.channelMaturityRadar')}
              </h4>
            </div>
            {/* Channel filter buttons */}
            {radarData?.data && radarData.data.length > 1 && (
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setSelectedRadarChannel(null)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    selectedRadarChannel === null
                      ? 'bg-[var(--color-primary-start)] text-white'
                      : 'bg-white text-[var(--color-charcoal)]/70 hover:bg-gray-100'
                  }`}
                >
                  {t('results.cards.kommunikation.overlay.all')}
                </button>
                {radarData.data.slice(0, 3).map((channel, idx) => (
                  <button
                    key={channel.kanal}
                    type="button"
                    onClick={() => setSelectedRadarChannel(idx)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      selectedRadarChannel === idx
                        ? 'text-white'
                        : 'bg-white text-[var(--color-charcoal)]/70 hover:bg-gray-100'
                    }`}
                    style={{
                      backgroundColor: selectedRadarChannel === idx ? CHANNEL_COLORS[idx] : undefined,
                    }}
                  >
                    {channel.kanal}
                  </button>
                ))}
              </div>
            )}
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="channelMaturity" />
          </div>
          <p className="text-xs text-[var(--color-charcoal)]/60 mb-2">
            {main['Kommunikation_Kanal-Reifegrad-Radar']?.notes?.interpretation_hint || ''}
          </p>
          {/* Fixed height container to prevent layout shift when legend toggles */}
          <div className="h-[300px]">
            {radarChartConfig ? (
              <AnalysisChart config={radarChartConfig} height={300} />
            ) : (
              <div className="h-full flex items-center justify-center">
                <span className="text-sm text-[var(--color-charcoal)]/50">
                  {t('results.cards.kommunikation.overlay.noDataAvailable')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary section */}
      {main.Kommunikation_Zusammenfassung && main.Kommunikation_Zusammenfassung !== '[placeholder]' && (
        <div className="pt-4 border-t border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5 text-[var(--color-primary-start)]" />
            <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">
              {t('results.cards.kommunikation.overlay.summary')}
            </h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="summary" />
          </div>
          <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
            <p className="text-sm text-[var(--color-charcoal)]/80 leading-relaxed">
              {main.Kommunikation_Zusammenfassung}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default KommunikationOverlayContent;
